<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  ref,
  getCurrentInstance,
  watch,
} from 'vue';
import Icon from 'next/icon/Icon.vue';
import snakecaseKeys from 'snakecase-keys';
import AiIdentity from 'dashboard/components-next/ai/AiIdentity.vue';
import AiUsageReceipt from 'dashboard/components-next/ai/AiUsageReceipt.vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { downloadFile } from '@chatwoot/utils';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
  canTranscribe: { type: Boolean, default: false },
  isTranscribing: { type: Boolean, default: false },
  transcriptionError: { type: String, default: '' },
  showTranscribedText: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['transcribe']);

defineOptions({
  inheritAttrs: false,
});

// Áudios recebidos em tempo real podem chegar antes de o arquivo estar disponível (ou ter
// a URL atualizada logo depois). O src fica ligado direto ao <audio>, muda só quando o
// arquivo muda (o navegador então recarrega) e falhas de carga são repetidas algumas vezes.
const LOAD_RETRY_DELAYS_MS = [1500, 3000, 6000];
const audioSrc = ref('');
const loadFailed = ref(false);
let loadRetries = 0;
let retryTimer = null;

const clearRetryTimer = () => {
  if (retryTimer) window.clearTimeout(retryTimer);
  retryTimer = null;
};

const buildSource = () => {
  if (!attachment.dataUrl) return '';
  try {
    return timeStampAppendedURL(attachment.dataUrl);
  } catch {
    return attachment.dataUrl;
  }
};

const TRANSCRIPT_PREVIEW_LENGTH = 200;
const isTranscriptExpanded = ref(false);
const isTranscriptLong = computed(
  () => (attachment.transcribedText?.length || 0) > TRANSCRIPT_PREVIEW_LENGTH
);
const displayedTranscript = computed(() => {
  const text = attachment.transcribedText || '';
  if (!isTranscriptLong.value || isTranscriptExpanded.value) return text;
  return `${text.slice(0, TRANSCRIPT_PREVIEW_LENGTH).trimEnd()}…`;
});

const aiUsage = computed(() =>
  attachment.aiUsage ? snakecaseKeys(attachment.aiUsage, { deep: true }) : null
);

const audioPlayer = useTemplateRef('audioPlayer');

const isPlaying = ref(false);
const isMuted = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);

const { uid } = getCurrentInstance();

const resetPlayback = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  duration.value = 0;
};

watch(
  () => attachment.dataUrl,
  () => {
    clearRetryTimer();
    loadRetries = 0;
    loadFailed.value = false;
    resetPlayback();
    audioSrc.value = buildSource();
  },
  { immediate: true }
);

const reloadSource = () => {
  if (!attachment.dataUrl) return;
  const url = new URL(buildSource());
  url.searchParams.set('t', Date.now());
  audioSrc.value = url.toString();
};

const onLoadError = () => {
  if (!attachment.dataUrl) return;
  if (loadRetries >= LOAD_RETRY_DELAYS_MS.length) {
    loadFailed.value = true;
    return;
  }
  const delay = LOAD_RETRY_DELAYS_MS[loadRetries];
  loadRetries += 1;
  clearRetryTimer();
  retryTimer = window.setTimeout(reloadSource, delay);
};

onBeforeUnmount(clearRetryTimer);

// MediaRecorder-produced WebM/Opus blobs lack a Duration header → <audio>.duration
// resolves to Infinity until we seek past the end, which forces the engine to
// scan the file and compute the real length. Safe no-op for files with a real
// duration already (mp3/m4a/etc).
const resolveStreamingDuration = () => {
  const el = audioPlayer.value;
  if (!el) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    el.currentTime = 0;
    duration.value = el.duration;
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try {
    el.currentTime = Number.MAX_SAFE_INTEGER;
  } catch {
    el.removeEventListener('timeupdate', onTimeUpdate);
  }
};

const onLoadedMetadata = () => {
  loadFailed.value = false;
  loadRetries = 0;
  const d = audioPlayer.value?.duration;
  if (!Number.isFinite(d)) {
    resolveStreamingDuration();
    return;
  }
  duration.value = d;
};

const playbackSpeedLabel = computed(() => {
  return `${playbackSpeed.value}x`;
});

// There maybe a chance that the audioPlayer ref is not available
// When the onLoadMetadata is called, so we need to set the duration
// value when the component is mounted
onMounted(() => {
  const d = audioPlayer.value?.duration;
  if (Number.isFinite(d)) duration.value = d;
  audioPlayer.value.playbackRate = playbackSpeed.value;
});

// Listen for global audio play events and pause if it's not this audio
useEmitter('pause_playing_audio', currentPlayingId => {
  if (currentPlayingId !== uid && isPlaying.value) {
    try {
      audioPlayer.value.pause();
    } catch {
      /* ignore pause errors */
    }
    isPlaying.value = false;
  }
});

const formatTime = time => {
  if (!time || Number.isNaN(time)) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const toggleMute = () => {
  audioPlayer.value.muted = !audioPlayer.value.muted;
  isMuted.value = audioPlayer.value.muted;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
};

const seek = event => {
  const time = Number(event.target.value);
  audioPlayer.value.currentTime = time;
  currentTime.value = time;
};

const startPlayback = async () => {
  const player = audioPlayer.value;
  // Emit event to pause all other audio
  emitter.emit('pause_playing_audio', uid);
  isPlaying.value = true;
  try {
    await player.play();
  } catch {
    isPlaying.value = false;
  }
};

const playOrPause = async () => {
  const player = audioPlayer.value;
  if (!player) return;
  if (isPlaying.value) {
    player.pause();
    isPlaying.value = false;
    return;
  }
  // Sem dados carregados (ex.: o arquivo ainda não existia quando a mensagem chegou),
  // recarrega a fonte antes de tocar, sem exigir trocar de conversa.
  if (loadFailed.value || player.readyState === 0 || player.error) {
    loadFailed.value = false;
    loadRetries = 0;
    reloadSource();
    player.addEventListener('canplay', startPlayback, { once: true });
    return;
  }
  await startPlayback();
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  playbackSpeed.value = 1;
  audioPlayer.value.playbackRate = 1;
};

const changePlaybackSpeed = () => {
  const speeds = [1, 1.5, 2];
  const currentIndex = speeds.indexOf(playbackSpeed.value);
  const nextIndex = (currentIndex + 1) % speeds.length;
  playbackSpeed.value = speeds[nextIndex];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const downloadAudio = async () => {
  const { fileType, dataUrl, extension } = attachment;
  downloadFile({ url: dataUrl, type: fileType, extension });
};
</script>

<template>
  <audio
    ref="audioPlayer"
    controls
    class="hidden"
    playsinline
    preload="metadata"
    :src="audioSrc || undefined"
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
    @error="onLoadError"
  />
  <div
    v-bind="$attrs"
    class="rounded-xl w-full gap-2 p-1.5 bg-n-alpha-white flex flex-col items-center border border-n-container shadow-[0px_2px_8px_0px_rgba(94,94,94,0.06)]"
  >
    <div
      class="audio-controls grid w-full min-w-0 items-center gap-x-px gap-y-2"
    >
      <button
        type="button"
        :aria-label="
          $t(
            isPlaying
              ? 'CONVERSATION.AUDIO_CONTROLS.PAUSE'
              : 'CONVERSATION.AUDIO_CONTROLS.PLAY'
          )
        "
        class="audio-play p-0 border-0 size-8 shrink-0"
        @click="playOrPause"
      >
        <Icon
          v-if="isPlaying"
          class="size-8"
          icon="i-teenyicons-pause-small-solid"
        />
        <Icon v-else class="size-8" icon="i-teenyicons-play-small-solid" />
      </button>
      <div class="audio-time whitespace-nowrap tabular-nums text-[11px]">
        {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
      </div>
      <div class="audio-progress flex min-w-0 items-center">
        <input
          :aria-label="$t('CONVERSATION.AUDIO_CONTROLS.SEEK')"
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="w-full min-w-0 h-1 bg-n-slate-12/40 rounded-lg appearance-none cursor-pointer accent-current"
          @input="seek"
        />
      </div>
      <button
        class="border-0 w-8 h-7 grid place-content-center bg-n-alpha-2 hover:bg-alpha-3 rounded-2xl"
        type="button"
        :aria-label="$t('CONVERSATION.AUDIO_CONTROLS.SPEED')"
        @click="changePlaybackSpeed"
      >
        <span class="text-xs text-n-slate-11 font-medium">
          {{ playbackSpeedLabel }}
        </span>
      </button>
      <button
        class="p-0 border-0 size-7 grid place-content-center"
        type="button"
        :aria-label="$t('CONVERSATION.AUDIO_CONTROLS.MUTE')"
        @click="toggleMute"
      >
        <Icon v-if="isMuted" class="size-4" icon="i-lucide-volume-off" />
        <Icon v-else class="size-4" icon="i-lucide-volume-2" />
      </button>
      <button
        class="p-0 border-0 size-7 grid place-content-center"
        type="button"
        :aria-label="$t('CONVERSATION.AUDIO_CONTROLS.DOWNLOAD')"
        @click="downloadAudio"
      >
        <Icon class="size-4" icon="i-lucide-download" />
      </button>
    </div>

    <div v-if="canTranscribe" class="w-full px-1 pb-1">
      <button
        type="button"
        data-testid="transcribe-audio"
        class="flex items-center gap-2 rounded-lg border-0 bg-n-alpha-1 px-3 py-2 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-wait disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-slate-10"
        :disabled="isTranscribing"
        :aria-busy="isTranscribing"
        @click.stop="!isTranscribing && emit('transcribe')"
      >
        <AiIdentity class="text-base" aria-hidden="true" />
        <span aria-live="polite">{{
          isTranscribing
            ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.PROCESSING')
            : transcriptionError
              ? $t('CONVERSATION.AUDIO_TRANSCRIPTION.RETRY')
              : $t('CONVERSATION.AUDIO_TRANSCRIPTION.ACTION')
        }}</span>
      </button>
      <p
        v-if="transcriptionError && !isTranscribing"
        class="m-0 mt-2 text-xs text-n-ruby-11 break-words"
        role="alert"
      >
        {{ transcriptionError }}
      </p>
    </div>
    <p
      v-if="
        attachment.transcriptionComplete &&
        !attachment.transcribedText &&
        showTranscribedText
      "
      class="m-0 p-2 text-xs text-n-slate-11"
      role="status"
    >
      {{ $t('CONVERSATION.AUDIO_TRANSCRIPTION.NO_SPEECH') }}
    </p>

    <div
      v-if="attachment.transcribedText && showTranscribedText"
      class="text-n-slate-12 p-3 text-sm bg-n-alpha-1 rounded-lg w-full break-words"
    >
      {{ displayedTranscript }}
      <button
        v-if="isTranscriptLong"
        class="block mt-1 p-0 border-0 bg-transparent text-n-slate-11 hover:text-n-slate-12 font-medium"
        @click="isTranscriptExpanded = !isTranscriptExpanded"
      >
        {{
          isTranscriptExpanded
            ? $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_LESS')
            : $t('CONVERSATION.VOICE_CALL.TRANSCRIPT_SHOW_MORE')
        }}
      </button>
    </div>
    <AiUsageReceipt v-if="aiUsage" :usage="aiUsage" />
  </div>
</template>

<style scoped>
.audio-controls {
  grid-template-columns: 2rem minmax(5rem, 1fr) 2rem 1.75rem 1.75rem;
}
.audio-play {
  grid-row: span 2;
}
.audio-progress {
  grid-column: 2 / -1;
  grid-row: 1;
  min-height: 1rem;
}
</style>
